"use client";

import { SectionHeader, FeatureCard } from "@/components/ui/";
import EnterpriseBannerDe from "@/components/sections/de/shared/enterprise-banner";
import { motion, useReducedMotion } from "@/lib/motion";
import { scrollRevealViewport } from "@/lib/motion-viewport";
import { STATS } from "@/lib/constants";
import { STATS_IPS_DE } from "@/components/sections/de/shared/stats-de";
import FlagshipCard from "@/components/sections/products/shared/flagship-card";
import {
  GeoTargetVisual,
  RotationVisual,
} from "@/components/sections/products/shared/feature-visuals";
import IpPoolMock from "@/components/sections/products/residential-proxies/pricing-features/ip-pool-mock";
import SuccessRateMock from "@/components/sections/products/residential-proxies/pricing-features/success-rate-mock";

// German clone of residential-proxies/pricing-features — reuses the exact
// same visual mocks (IpPoolMock/SuccessRateMock carry a handful of English UI
// chrome labels like "Platform uptime", same category as the dashboard demos
// left in English), translated surrounding copy.

const supportingFeatures = [
  {
    Visual: GeoTargetVisual,
    title: "Präzises Geo-Targeting",
    description:
      "Targeting nach Land und Stadt. Jede Anfrage über genau den Standort leiten, den der Anwendungsfall benötigt, bis auf Stadtebene.",
  },
  {
    Visual: RotationVisual,
    title: "Rotation & Sticky Sessions",
    description:
      "IPs bei jeder Anfrage rotieren oder eine Session halten. Für Scraping, Monitoring und Account-Workflows, die beide Optionen benötigen.",
  },
];

const containerVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

export default function ResidentialPricingFeaturesDe() {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section
      id="product-features"
      className="py-section border-border border-b"
      aria-labelledby="residential-features-heading"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 max-w-2xl">
          <SectionHeader
            eyebrow="Warum Proxidize"
            id="residential-features-heading"
            heading={
              <>
                Für Leistung gebaut.
                <br />
                Für Skalierung bepreist.
              </>
            }
            body="Das ist in jedem Kauf enthalten."
          />
        </div>

        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="grid gap-4 sm:gap-5 lg:grid-cols-2"
        >
          <FlagshipCard
            title={`${STATS_IPS_DE} echte Residential-IPs`}
            description={`Premium-Residential-IPs, ethisch bezogen aus Opt-in-Geräten in ${STATS.countries} Ländern. Vollständig konform und ausschließlich für zugelassene Anwendungsfälle reserviert.`}
            reduced={prefersReducedMotion}
          >
            <IpPoolMock />
          </FlagshipCard>
          <FlagshipCard
            title="99,9 %+ Plattform-Verfügbarkeit"
            description="Residential Proxies können die Zugriffszuverlässigkeit für geeignete Workloads verbessern, die Ergebnisse hängen jedoch vom Ziel, Standort, der Session-Konfiguration, dem Anfrageverhalten und den Akzeptanzkriterien ab."
            reduced={prefersReducedMotion}
          >
            <SuccessRateMock />
          </FlagshipCard>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial={prefersReducedMotion ? "visible" : "hidden"}
          whileInView="visible"
          viewport={scrollRevealViewport}
          className="mt-4 grid gap-4 sm:mt-5 sm:grid-cols-2 sm:gap-5"
        >
          {supportingFeatures.map((feature) => (
            <FeatureCard
              key={feature.title}
              Visual={feature.Visual}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </motion.div>

        <div className="mt-10">
          <EnterpriseBannerDe description="Individuelle Rotationsregeln, priorisierter Support, höhere Volumengrenzen und Preise ab $0,5/GB." />
        </div>
      </div>
    </section>
  );
}
